import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ApiServices } from '../../services/ApiServices';
import { Loader2, Search, Calendar, Activity, LayoutGrid } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '../../components/ui/pagination';
import { cn } from '../../lib/utils';

const SECTION_LABELS = {
    'homepage': 'Home',
    'trends': 'Trends',
    'celebs': 'Celebs',
    'news': 'News',
    'settings': 'Settings',
    'login': 'Login',
    'profile': 'Profile',
    'trend_details': 'Trend details',
    'research': 'Research',
    'other': 'Other'
};

const AuditLogsDashboard = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(10);

    const [filters, setFilters] = useState({
        username: '',
        event_type: 'all',
        section: 'all',
        date_range: 'all',
        page: 1
    });

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { 
                ...filters,
                page_size: pageSize
            };
            if (params.event_type === 'all') delete params.event_type;
            if (params.section === 'all') delete params.section;
            if (params.date_range === 'all') delete params.date_range;
            if (!params.username) delete params.username;

            const data = await ApiServices.getAuditLogs(params);
            setLogs(data.logs || []);
            const total = data.total_count || 0;
            setTotalItems(total);
            setTotalPages(Math.ceil(total / pageSize));
        } catch (error) {
            console.error('Failed to fetch audit logs:', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLogs();
        }, 300); // Debounce
        return () => clearTimeout(timer);
    }, [fetchLogs]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const d = new Date(dateString);
        return d.toLocaleString();
    };

    const getEventBadge = (type) => {
        switch (type?.toLowerCase()) {
            case 'login': return <Badge className="bg-green-500/10 text-green-700 hover:bg-green-500/20">Login</Badge>;
            case 'logout': return <Badge className="bg-orange-500/10 text-orange-700 hover:bg-orange-500/20">Logout</Badge>;
            case 'page_view': return <Badge className="bg-blue-500/10 text-blue-700 hover:bg-blue-500/20">Page View</Badge>;
            default: return <Badge variant="outline" className="capitalize">{type}</Badge>;
        }
    };

    return (
        <Card className="mt-8 relative overflow-hidden">
            <CardHeader className="bg-muted/30 pb-4 border-b">
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                    <Activity className="h-5 w-5 text-primary" />
                    История событий
                </CardTitle>
                <CardDescription>Мониторинг активности пользователей и системных событий.</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border-b bg-card">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Поиск пользователя"
                            value={filters.username}
                            onChange={(e) => handleFilterChange('username', e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    
                    <Select value={filters.event_type} onValueChange={(v) => handleFilterChange('event_type', v)}>
                        <SelectTrigger>
                            <Activity className="w-4 h-4 mr-2 opacity-50" />
                            <SelectValue placeholder="Тип события" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Все события</SelectItem>
                            <SelectItem value="login">Login</SelectItem>
                            <SelectItem value="logout">Logout</SelectItem>
                            <SelectItem value="page_view">Page View</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.section} onValueChange={(v) => handleFilterChange('section', v)}>
                        <SelectTrigger>
                            <LayoutGrid className="w-4 h-4 mr-2 opacity-50" />
                            <SelectValue placeholder="Раздел" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Все разделы</SelectItem>
                            <SelectItem value="homepage">Home</SelectItem>
                            <SelectItem value="trends">Trends</SelectItem>
                            <SelectItem value="celebs">Celebs</SelectItem>
                            <SelectItem value="news">News</SelectItem>
                            <SelectItem value="settings">Settings</SelectItem>
                            <SelectItem value="login">Login</SelectItem>
                            <SelectItem value="profile">Profile</SelectItem>
                            <SelectItem value="trend_details">Trend Details</SelectItem>
                            <SelectItem value="research">Research</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                    </Select>

                    <Select value={filters.date_range} onValueChange={(v) => handleFilterChange('date_range', v)}>
                        <SelectTrigger>
                            <Calendar className="w-4 h-4 mr-2 opacity-50" />
                            <SelectValue placeholder="Период" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">За все время</SelectItem>
                            <SelectItem value="1d">За последние 24 часа</SelectItem>
                            <SelectItem value="2d">За последние 48 часов</SelectItem>
                            <SelectItem value="1w">За последнюю неделю</SelectItem>
                            <SelectItem value="1m">За последний месяц</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-muted-foreground uppercase bg-muted/50 border-b">
                            <tr>
                                <th className="px-6 py-3 font-medium">Timestamp</th>
                                <th className="px-6 py-3 font-medium">Username</th>
                                <th className="px-6 py-3 font-medium">Event Type</th>
                                <th className="px-6 py-3 font-medium text-right">Section</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-muted-foreground">
                                        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 opacity-50" />
                                        Loading logs...
                                    </td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-muted-foreground">
                                        No audit logs found matching your criteria.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-muted/30 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-muted-foreground">
                                            {formatDate(log.created_at)}
                                        </td>
                                        <td className="px-6 py-4 font-medium">
                                            {log.username}
                                        </td>
                                        <td className="px-6 py-4">
                                            {getEventBadge(log.event_type)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="capitalize">{SECTION_LABELS[log.section] || log.section || '-'}</span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                    <div className="p-4 border-t bg-card/50 flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                            Показана страница {filters.page} из {totalPages} (всего {totalItems})
                        </span>
                        <Pagination className="w-auto mx-0">
                            <PaginationContent>
                                <PaginationItem>
                                    <PaginationPrevious
                                        onClick={() => filters.page > 1 && setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                                        className={cn("cursor-pointer", filters.page === 1 && "pointer-events-none opacity-50")}
                                    />
                                </PaginationItem>

                                {Array.from({ length: totalPages }).map((_, i) => {
                                    const p = i + 1;
                                    // Logic for showing first, last, and pages around current
                                    if (p === 1 || p === totalPages || (p >= filters.page - 1 && p <= filters.page + 1)) {
                                        return (
                                            <PaginationItem key={p}>
                                                <PaginationLink
                                                    isActive={filters.page === p}
                                                    onClick={() => setFilters(prev => ({ ...prev, page: p }))}
                                                    className="cursor-pointer"
                                                >
                                                    {p}
                                                </PaginationLink>
                                            </PaginationItem>
                                        );
                                    }
                                    if (p === filters.page - 2 || p === filters.page + 2) {
                                        return <PaginationItem key={p}><PaginationEllipsis /></PaginationItem>;
                                    }
                                    return null;
                                })}

                                <PaginationItem>
                                    <PaginationNext
                                        onClick={() => filters.page < totalPages && setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                                        className={cn("cursor-pointer", filters.page === totalPages && "pointer-events-none opacity-50")}
                                    />
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default AuditLogsDashboard;
